import { DeepPartial, DeepReadonly } from 'ts-essentials';
import { ITextRunStyle } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import { numToPercent, percentToNum, ptToPx, pxToPt } from '@src/slide/infrastructure/module/independent/unit';
import { ICTTextCharacterProperties, DeltaOf, ICTColorDmlA } from '@tencent/slide-sdk-model';
import { colorStrToColorDmlA, getEgColorChoiceRgba } from '@src/slide/infrastructure/module/independent/color/transform';
import { ILinkAttr, LinkConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/link/link-converter';
import { TEXT_BASELINE_OFFSET } from '@src/slide/domain/ui-model/element-modeles/property/text/constants';
import { BlipFillConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/fill/blip-fill/blip-fill-converter';
import { GradientFillConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/fill/gradient-fill/gradient-fill-converter';
import { PatternFillConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/fill/pattern-fill/pattern-fill-converter';
import { BorderConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/border/border-converter';
import { EffectConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/effect/effect-converter';
import { TEXT_STRIKE_TYPE_MAP } from '@src/slide/domain/ui-model/converter/node-property/sdk-ui-enum-map/text-strike-type';
import { invertMap } from '@src/slide/infrastructure/module/independent/object/map-invert';
import _invert from 'lodash/invert';
import _set from 'lodash/set';
import {
    ColorConverter,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/color/color-converter';
import { IEGColorChoice } from '@tencent/slide-sdk-pb-model/src/ultrabuf/ultrabuf.types';
import { ITDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { TEXT_UNDERLINE_TYPE_SDK2UI_MAP, TEXT_UNDERLINE_TYPE_UI2SDK_MAP } from '@src/slide/domain/ui-model/converter/node-property/sdk-ui-enum-map/text-underline-type';

/**
 * 文字属性
 */
export class TextRunStyleConverter {
    /**
     * 文字属性的sdk -> ui 映射
     * @param sdkData
     * @param uiData
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextCharacterProperties>>,
        uiData: ITextRunStyle,
    ): void {
        // 下划线
        TextRunStyleConverter.convertUnderLineSdkToUi(sdkData, uiData);
        // 删除线
        TextRunStyleConverter.convertDelLineSdkToUi(sdkData, uiData);
        // 字体
        TextRunStyleConverter.convertFontSdkToUi(sdkData, uiData);
        // 语言类型
        TextRunStyleConverter.convertLangSdkToUi(sdkData, uiData);
        // 字号
        TextRunStyleConverter.convertFontSizeSdkToUi(sdkData, uiData);
        // 粗体
        TextRunStyleConverter.convertBoldSdkToUi(sdkData, uiData);
        // 斜体
        TextRunStyleConverter.convertItalicSdkToUi(sdkData, uiData);
        // 字符间距
        TextRunStyleConverter.convertLetterSpaceSdkToUi(sdkData, uiData);
        // 突出显示
        TextRunStyleConverter.convertHighlightSdkToUi(sdkData, uiData);
        // 链接（link）
        TextRunStyleConverter.convertLinkSdkToUi(sdkData, uiData);
        // 文字基线（上下标）
        TextRunStyleConverter.convertBaseLineSdkToUi(sdkData, uiData);
        // 填充（无填充、纯色、图片、渐变、图案）
        TextRunStyleConverter.convertFillSdkToUi(sdkData, uiData);
        // 边框（无边框、纯色、渐变）
        TextRunStyleConverter.convertBorderSdkToUi(sdkData, uiData);
        // 效果（阴影、映像、发光）
        TextRunStyleConverter.convertEffectSdkToUi(sdkData, uiData);
    }

    /**
     * 文字属性的ui-> sdk 映射
     * @param uiData
     * @param sdkData
     */
    public static convertPartialDataFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextRunStyle>>,
        sdkData: ICTTextCharacterProperties,
    ): void {
        // 下划线
        TextRunStyleConverter.convertUnderLineUiToSdk(uiData, sdkData);
        // 删除线
        TextRunStyleConverter.convertDelLineUiToSdk(uiData, sdkData);
        // 字体
        TextRunStyleConverter.convertFontUiToSdk(uiData, sdkData);
        // 语言类型
        TextRunStyleConverter.convertLangUiToSdk(uiData, sdkData);
        // 字号
        TextRunStyleConverter.convertFontSizeUiToSdk(uiData, sdkData);
        // 粗体
        TextRunStyleConverter.convertBoldUiToSdk(uiData, sdkData);
        // 斜体
        TextRunStyleConverter.convertItalicUiToSdk(uiData, sdkData);
        // 字符间距
        TextRunStyleConverter.convertLetterSpaceUiToSdk(uiData, sdkData);
        // 突出显示
        TextRunStyleConverter.convertHighlightUiToSdk(uiData, sdkData);
        // 链接（link）
        TextRunStyleConverter.convertLinkUiToSdk(uiData, sdkData);
        // 文字基线（上下标）
        TextRunStyleConverter.convertBaseLineUiToSdk(uiData, sdkData);
        // 填充（无填充、纯色、图片、渐变、图案）
        TextRunStyleConverter.convertFillUiToSdk(uiData, sdkData);
        // 边框（无边框、纯色、渐变）
        TextRunStyleConverter.convertBorderUiToSdk(uiData, sdkData);
        // 效果（阴影、映像、发光）
        TextRunStyleConverter.convertEffectUiToSdk(uiData, sdkData);
    }

    /**
     * 下划线
     * @param sdkData
     * @param uiData
     */
    private static convertUnderLineSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextCharacterProperties>>,
        uiData: ITextRunStyle,
    ): void {
        if (sdkData.u) {
            uiData.textDecoration = TEXT_UNDERLINE_TYPE_SDK2UI_MAP.get(sdkData.u);
        } else {
            delete uiData.textDecoration;
        }
    }

    /**
     * 删除线
     * @param sdkData
     * @param uiData
     */
    private static convertDelLineSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextCharacterProperties>>,
        uiData: ITextRunStyle,
    ): void {
        const { strike } = sdkData;
        if (strike) {
            uiData.del = TEXT_STRIKE_TYPE_MAP.get(strike);
        } else {
            delete uiData.del;
        }
    }

    /**
     * 字符间距
     * @param sdkData
     * @param uiData
     */
    private static convertLetterSpaceSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextCharacterProperties>>,
        uiData: ITextRunStyle,
    ): void {
        const strike = sdkData.spc;
        if (strike || strike === 0) {
            uiData.letterSpacing = strike / 100;
        } else {
            delete uiData.letterSpacing;
        }
    }

    /**
     * 字体
     * @param sdkData
     * @param uiData
     */
    private static convertFontSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextCharacterProperties>>,
        uiData: ITextRunStyle,
    ): void {
        const ea = sdkData.ea?.typeface;
        const latin = sdkData.latin?.typeface;
        const cs = sdkData.cs?.typeface;
        const sym = sdkData.sym?.typeface;
        if (!ea && !latin && !cs && !sym) {
            delete uiData.fonts;
            return;
        }
        uiData.fonts = {};
        if (ea) {
            uiData.fonts.ea = ea;
        }
        if (latin) {
            uiData.fonts.latin = latin;
        }
        if (cs) {
            uiData.fonts.cs = cs;
        }
        if (sym) {
            uiData.fonts.sym = sym;
        }
    }

    /**
     * 语言类型
     * @param sdkData
     * @param uiData
     */
    private static convertLangSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextCharacterProperties>>,
        uiData: ITextRunStyle,
    ): void {
        const { lang } = sdkData;
        if (lang) {
            uiData.lang = { en: 'latin', 'en-US': 'latin', 'zh-CN': 'ea' }[lang] || lang;
        } else {
            delete uiData.lang;
        }
    }

    /**
     * 字号
     * @param sdkData
     * @param uiData
     */
    private static convertFontSizeSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextCharacterProperties>>,
        uiData: ITextRunStyle,
    ): void {
        if (sdkData.sz) {
            uiData.fontSize = ptToPx(sdkData.sz);
        } else {
            delete uiData.fontSize;
        }
    }

    /**
     * 粗体
     * @param sdkData
     * @param uiData
     */
    private static convertBoldSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextCharacterProperties>>,
        uiData: ITextRunStyle,
    ): void {
        const { b } = sdkData;
        if (b === true || b === false) {
            uiData.fontWeight = b ? 'bold' : 'normal';
        } else {
            delete uiData.fontWeight;
        }
    }

    /**
     * 斜体
     * @param sdkData
     * @param uiData
     */
    private static convertItalicSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextCharacterProperties>>,
        uiData: ITextRunStyle,
    ): void {
        const { i } = sdkData;
        if (i === true || i === false) {
            uiData.fontStyle = i ? 'italic' : 'normal';
        } else {
            delete uiData.fontStyle;
        }
    }

    /**
     * 突出显示
     * @param sdkData
     * @param uiData
     */
    private static convertHighlightSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextCharacterProperties>>,
        uiData: ITextRunStyle,
    ): void {
        const srgbClr = sdkData.highlight?.srgbClr;
        if (srgbClr) {
            uiData.backgroundColor = getEgColorChoiceRgba(srgbClr);
        } else {
            delete uiData.backgroundColor;
        }
    }

    /**
     * 链接
     * @param sdkData
     * @param uiData
     */
    private static convertLinkSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextCharacterProperties>>,
        uiData: ITextRunStyle,
    ): void {
        const link = sdkData.hlinkClick;
        if (link) {
            LinkConverter.convertPartialDataFromSdkToUi(link, uiData, ILinkAttr.href);
        } else {
            delete uiData.href;
        }
    }

    /**
     * 文字基线（上下标）
     * @param sdkData
     * @param uiData
     */
    private static convertBaseLineSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextCharacterProperties>>,
        uiData: ITextRunStyle,
    ): void {
        const baseLine = sdkData.baseline;
        if (baseLine || baseLine === 0) {
            uiData[TEXT_BASELINE_OFFSET] = percentToNum(baseLine);
        } else {
            delete uiData[TEXT_BASELINE_OFFSET];
        }
    }

    /**
     * 填充
     * @param sdkData
     * @param uiData
     */
    // eslint-disable-next-line complexity
    private static convertFillSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextCharacterProperties>>,
        uiData: ITextRunStyle,
    ): void {
        // 无填充
        if (sdkData.noFill) {
            uiData.noFill = 1;
        } else {
            delete uiData.noFill;
        }
        // 纯色填充
        const {
            srgbClr, schemeClr, prstClr, scrgbClr, hslClr, sysClr,
        } = sdkData.solidFill?.egColorChoice || {};
        const otherClr = schemeClr || prstClr || scrgbClr || hslClr || sysClr;
        const egColorChoice = sdkData.solidFill?.egColorChoice;
        if (egColorChoice) {
            const tdColor: ITDColor = ColorConverter.convertPartialDataFromSdkToUi(egColorChoice);
            uiData.tdColor = tdColor;
            uiData.color = uiData.tdColor?.srgb_color;
        } else if (otherClr) {
            // 正常情况，上层无需关注otherClr，不论是继承的还是用户编辑的，sdk均会转为srgbClr
            // 但是addParagraphListRawSignProps时会转一遍ownData用于对比，如果是otherClr，则也算用户编辑过的，需要保留此字段用于raw打点
            uiData.color = 'otherClr';
        } else {
            delete uiData.color;
        }
        // 图片填充
        if (sdkData.blipFill?.blip) {
            uiData.image = BlipFillConverter.convertPartialDataFromSdkToUi(sdkData.blipFill);
        } else {
            delete uiData.image;
        }
        // 渐变填充
        if (sdkData.gradFill?.gsLst) {
            uiData.gradFill = GradientFillConverter.convertPartialDataFromSdkToUi(sdkData.gradFill);
        } else {
            delete uiData.gradFill;
        }
        // 图案填充
        if (sdkData.pattFill) {
            uiData.pattFill = PatternFillConverter.convertPartialDataFromSdkToUi(sdkData.pattFill);
        } else {
            delete uiData.pattFill;
        }
    }

    /**
     * 边框
     * @param sdkData
     * @param uiData
     */
    private static convertBorderSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextCharacterProperties>>,
        uiData: ITextRunStyle,
    ): void {
        if (sdkData.ln) {
            uiData.border = {};
            BorderConverter.convertPartialDataFromSdkToUi(sdkData.ln, uiData.border);
        } else {
            delete uiData.border;
        }
    }

    /**
     * 效果
     * @param sdkData
     * @param uiData
     */
    private static convertEffectSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextCharacterProperties>>,
        uiData: ITextRunStyle,
    ): void {
        if (sdkData.effectLst) {
            uiData.effect = {};
            EffectConverter.convertPartialDataFromSdkToUi(sdkData.effectLst, uiData.effect);
        } else {
            delete uiData.effect;
        }
    }

    /**
     * 下划线
     * @param uiData
     * @param sdkData
     */
    private static convertUnderLineUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextRunStyle>>,
        sdkData: DeltaOf<ICTTextCharacterProperties>,
    ): void {
        if (uiData.textDecoration) {
            sdkData.u = TEXT_UNDERLINE_TYPE_UI2SDK_MAP.get(uiData.textDecoration);
        } else if (uiData.textDecoration === null) {
            sdkData.u = undefined;
        }
    }

    /**
     * 删除线
     * @param uiData
     * @param sdkData
     */
    private static convertDelLineUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextRunStyle>>,
        sdkData: DeltaOf<ICTTextCharacterProperties>,
    ): void {
        if (uiData.del) {
            sdkData.strike = invertMap(TEXT_STRIKE_TYPE_MAP).get(uiData.del);
        } else if (uiData.del === null) {
            sdkData.strike = undefined;
        }
    }

    /**
     * 字体
     * @param uiData
     * @param sdkData
     */
    private static convertFontUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextRunStyle>>,
        sdkData: DeltaOf<ICTTextCharacterProperties>,
    ): void {
        if (uiData.fonts?.latin) {
            _set(sdkData, 'latin.typeface', uiData.fonts?.latin);
        } else if (uiData.fonts?.latin === null) {
            _set(sdkData, 'latin.typeface', undefined);
        }
        if (uiData.fonts?.ea) {
            _set(sdkData, 'ea.typeface', uiData.fonts.ea);
        } else if (uiData.fonts?.ea === null) {
            _set(sdkData, 'ea.typeface', undefined);
        }
        if (uiData.fonts?.cs) {
            _set(sdkData, 'cs.typeface', uiData.fonts.cs);
        } else if (uiData.fonts?.cs === null) {
            _set(sdkData, 'cs.typeface', undefined);
        }
        if (uiData.fonts?.sym) {
            _set(sdkData, 'sym.typeface', uiData.fonts.sym);
        } else if (uiData.fonts?.sym === null) {
            _set(sdkData, 'sym.typeface', undefined);
        }
    }

    /**
     * 语言类型
     * @param uiData
     * @param sdkData
     */
    private static convertLangUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextRunStyle>>,
        sdkData: DeltaOf<ICTTextCharacterProperties>,
    ): void {
        if (uiData.lang) {
            sdkData.lang = _invert({ en: 'latin', 'en-US': 'latin', 'zh-CN': 'ea' })[uiData.lang];
        } else if (uiData.lang === null) {
            sdkData.lang = undefined;
        }
    }

    /**
     * 字号
     * @param uiData
     * @param sdkData
     */
    private static convertFontSizeUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextRunStyle>>,
        sdkData: DeltaOf<ICTTextCharacterProperties>,
    ): void {
        if (uiData.fontSize) {
            sdkData.sz = pxToPt(uiData.fontSize);
        } else if (uiData.fontSize === null) {
            sdkData.sz = undefined;
        }
    }

    /**
     * 粗体
     * @param uiData
     * @param sdkData
     */
    private static convertBoldUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextRunStyle>>,
        sdkData: DeltaOf<ICTTextCharacterProperties>,
    ): void {
        if (uiData.fontWeight) {
            sdkData.b = uiData.fontWeight === 'bold';
        } else if (uiData.fontWeight === null) {
            sdkData.b = undefined;
        }
    }

    /**
     * 斜体
     * @param uiData
     * @param sdkData
     */
    private static convertItalicUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextRunStyle>>,
        sdkData: DeltaOf<ICTTextCharacterProperties>,
    ): void {
        if (uiData.fontStyle) {
            sdkData.i = uiData.fontStyle === 'italic';
        } else if (uiData.fontStyle === null) {
            sdkData.i = undefined;
        }
    }

    /**
     * 字符间距
     * @param uiData
     * @param sdkData
     */
    private static convertLetterSpaceUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextRunStyle>>,
        sdkData: DeltaOf<ICTTextCharacterProperties>,
    ): void {
        if (uiData.letterSpacing || uiData.letterSpacing === 0) {
            sdkData.spc = uiData.letterSpacing * 100;
        } else if (uiData.letterSpacing === null) {
            sdkData.spc = undefined;
        }
    }

    /**
     * 突出显示
     * @param uiData
     * @param sdkData
     */
    private static convertHighlightUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextRunStyle>>,
        sdkData: DeltaOf<ICTTextCharacterProperties>,
    ): void {
        if (uiData.backgroundColor) {
            sdkData.highlight = colorStrToColorDmlA(uiData.backgroundColor) as ICTColorDmlA;
        } else if (uiData.backgroundColor === null) {
            sdkData.highlight = undefined;
        }
    }

    /**
     * 链接
     * @param uiData
     * @param sdkData
     */
    private static convertLinkUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextRunStyle>>,
        sdkData: DeltaOf<ICTTextCharacterProperties>,
    ): void {
        if (uiData[ILinkAttr.href]) {
            sdkData.hlinkClick = {};
            LinkConverter.convertPartialDataFromUiToSdk(uiData, sdkData.hlinkClick, ILinkAttr.href);
        } else {
            // 调用处没传null，所以只能在这判断没有值均赋为null
            sdkData.hlinkClick = undefined;
        }
    }

    /**
     * 文字基线（上下标）
     * @param uiData
     * @param sdkData
     */
    private static convertBaseLineUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextRunStyle>>,
        sdkData: DeltaOf<ICTTextCharacterProperties>,
    ): void {
        if (uiData[TEXT_BASELINE_OFFSET] || uiData[TEXT_BASELINE_OFFSET] === 0) {
            sdkData.baseline = numToPercent(uiData[TEXT_BASELINE_OFFSET]);
        } else if (uiData[TEXT_BASELINE_OFFSET] === null) {
            sdkData.baseline = undefined;
        }
    }

    /**
     * 填充
     * @param uiData
     * @param sdkData
     */
    // eslint-disable-next-line complexity
    private static convertFillUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextRunStyle>>,
        sdkData: DeltaOf<ICTTextCharacterProperties>,
    ): void {
        // 无填充
        if (uiData.noFill) {
            sdkData.noFill = true;
        } else if (uiData.noFill === null) {
            sdkData.noFill = undefined;
        } else {
            // 纯色填充
            if (uiData.tdColor && uiData.color) {
                const egColorChoice: IEGColorChoice = {};
                ColorConverter.convertPartialDataFromUiToSdk(uiData.tdColor, egColorChoice);
                _set(sdkData, 'solidFill.egColorChoice', egColorChoice);
            } else if (!uiData.color) {
                sdkData.solidFill = null;
            }
        }

        // 图片填充
        if (uiData.image) {
            sdkData.blipFill = BlipFillConverter.convertPartialDataFromUiToSdk(uiData.image);
        } else if (uiData.image === null) {
            sdkData.blipFill = undefined;
        }
        // 渐变填充
        if (uiData.gradFill) {
            sdkData.gradFill = GradientFillConverter.convertPartialDataFromUiToSdk(uiData.gradFill);
        } else if (uiData.gradFill === null) {
            sdkData.gradFill = undefined;
        }
        // 图案填充
        if (uiData.pattFill) {
            sdkData.pattFill = PatternFillConverter.convertPartialDataFromUiToSdk(uiData.pattFill);
        } else {
            sdkData.pattFill = undefined;
        }
    }

    /**
     * 边框
     * @param uiData
     * @param sdkData
     */
    private static convertBorderUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextRunStyle>>,
        sdkData: DeltaOf<ICTTextCharacterProperties>,
    ): void {
        if (uiData.border) {
            sdkData.ln = {};
            BorderConverter.convertPartialDataFromUiToSdk(uiData.border, sdkData.ln);
        } else if (uiData.border === null) {
            sdkData.ln = undefined;
        }
    }

    /**
     * 效果
     * @param sdkData
     * @param uiData
     */
    private static convertEffectUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITextRunStyle>>,
        sdkData: DeltaOf<ICTTextCharacterProperties>,
    ): void {
        if (uiData.effect) {
            sdkData.effectLst = {};
            EffectConverter.convertPartialDataFromUiToSdk(uiData.effect, sdkData.effectLst);
        } else if (uiData.effect === null) {
            sdkData.effectLst = undefined;
        }
    }
}
